import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Collapse, alpha, makeStyles } from '@material-ui/core';
import DotsMenu from '../../dotsMenu';
import { prepareMenuItems } from '../../../../utility/divisions';
import CustomButton from '../../customButton';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import ActionButton from '../../actionButton';
import { isArrayEmpty, reorder } from '../../../../utility/helpers';
import { ACTION_BUTTON_TYPES } from '../../actionButton/config';
import Department from './department';
import ReorderItems from '../../reorderItems';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    header: {
      backgroundColor: primary.bluish8,
      borderRadius: 8,
      boxSizing: 'border-box',
      border: `1px solid ${primary.bluish6}`,
      marginBottom: spacing(4),
      cursor: 'pointer',
      alignItems: 'center',
      height: 40,
      display: 'grid',
      gridTemplateColumns:
        'minmax(36px, 36px) minmax(140px, 1317px) minmax(160px, 170px) minmax(60px, 60px)',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns:
          'minmax(36px, 36px) minmax(140px, 1980px) minmax(160px, 170px) minmax(60px, 60px)',
      },
      '& $dragButton': {
        opacity: 0,
        transition: 'opacity .3s ease',
      },
      '&:hover': {
        boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.17)}`,
        transition: 'box-shadow .3s ease',
        '& $dragButton': {
          opacity: ({ isVisible }) => {
            return isVisible ? 1 : 0;
          },
          transition: 'opacity .3s ease',
          cursor: ({ isVisible }) => {
            return isVisible ? 'pointer' : 'grabbing';
          },
        },
      },
      '&:active': {
        '& > div:first-of-type': {
          opacity: 1,
          transition: 'opacity .3s ease',
        },
        boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.17)}`,
        transition: 'box-shadow .3s ease',
      },
    },
    headerDisabled: {
      gridTemplateColumns:
        'minmax(90px, 1271px) minmax(160px, 190px) minmax(30px, 40px)',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns:
          'minmax(90px, 1986px) minmax(160px, 170px) minmax(30px, 40px)',
      },
    },
    headerWithDelete: {
      gridTemplateColumns:
        'minmax(140px, 1317px) minmax(160px, 170px) minmax(60px, 60px)',
      [breakpoints.up('xLg')]: {
        gridTemplateColumns:
          'minmax(140px, 1980px) minmax(160px, 170px) minmax(60px, 60px)',
      },
    },
    disableCollapse: {
      cursor: 'default',
    },
    actionsWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(1),
      marginRight: spacing(2),
      justifyContent: 'flex-end',
    },
    templateActionButton: {
      marginRight: 0,
    },
    actionButton: {
      width: 24,
      height: 24,
    },
    expandIcon: {
      transform: 'rotate(0deg)',
      transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
    },
    expandIconOpened: {
      transform: 'rotate(180deg)',
    },
    titleText: {
      boxSizing: 'border-box',
      fontSize: 14,
      lineHeight: '18px',
      paddingLeft: spacing(9),
    },
    titleWithDragButton: {
      paddingLeft: 0,
    },
    button: {
      borderRadius: 8,
      marginBottom: spacing(4),
      marginTop: spacing(1),
      width: '100%',
      justifyContent: 'center',
    },
    dragButton: {
      position: 'relative',
      width: 22,
      height: 22,
      marginLeft: spacing(2),
      flexShrink: 0,
      marginRight: spacing(1.5),
      zIndex: 11,
      '& button': {
        width: 22,
        height: 22,
      },
      '$header:hover': {
        opacity: ({ isVisible }) => {
          return isVisible ? 1 : 0;
        },
        transition: 'opacity .3s ease',
        cursor: ({ isVisible }) => {
          return isVisible ? 'pointer' : 'grabbing';
        },
      },
    },
    dragIcon: {
      width: 8,
      height: 14,
    },
    dragTooltipLabel: {
      fontFamily: 'ProximaNova-Regular',
    },
    departmentsWrapper: {
      marginLeft: 40,
    },
    departmentItem: {
      position: 'relative',
      '&:last-of-type': {
        '& > div': {
          borderRadius: 8,
          borderBottom: 'none',
          position: 'relative',
        },
      },
    },
    peopleLabel: {
      marginLeft: 0,
      fontSize: 14,
      lineHeight: '18px',
    },
  })
);

const Division = ({
  translations,
  division,
  dragRef = () => {},
  dragProps = null,
  isVisible = false,
  canEdit,
  canDelete,
  onDuplicateDivision = () => {},
  onEditDivisionTitle = () => {},
  handleDeleteDivisionDialog = () => {},
  handleCreateDepartment = () => {},
  onReorderDepartment = () => {},
}) => {
  const classes = useStyles({ isVisible });
  const { name, departments } = division;
  const hasDepartments = !isArrayEmpty(departments);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    if (hasDepartments || canEdit) {
      setIsExpanded(prevExpanded => !prevExpanded);
    }
  };

  const handleReorder = useCallback(
    result => {
      if (
        !result.destination ||
        result.source.index === result.destination.index
      )
        return;

      const updatedOrder = reorder(
        departments,
        result.source.index,
        result.destination.index
      );

      return onReorderDepartment(division?.id, updatedOrder);
    },
    [departments, division, onReorderDepartment]
  );

  return (
    <div ref={dragRef} {...(dragProps ? { ...dragProps.draggableProps } : {})}>
      <div
        className={classNames(classes.header, {
          [classes.headerDisabled]: !canEdit && !canDelete,
          [classes.headerWithDelete]: !canEdit && canDelete,
          [classes.disableCollapse]: !hasDepartments,
        })}
        onClick={toggleExpand}
      >
        {canEdit && (
          <ActionButton
            className={classes.dragButton}
            iconClass={classes.dragIcon}
            tooltipLabelClass={classes.dragTooltipLabel}
            type={ACTION_BUTTON_TYPES.DRAG}
            tooltipText={translations.divisionDragTooltip}
            {...(dragProps ? { ...dragProps.dragHandleProps } : {})}
          />
        )}
        <TextBoxWithTooltip
          className={classNames(classes.titleText, {
            [classes.titleWithDragButton]: canEdit,
          })}
          variant="h5"
          text={name}
        />
        <TextBoxWithTooltip
          className={classes.peopleLabel}
          variant="h5"
          text={translations.people}
        />
        <div className={classes.actionsWrapper}>
          {(canEdit || canDelete) && (
            <DotsMenu
              menuItems={prepareMenuItems(
                translations,
                division,
                division,
                canEdit,
                canDelete,
                handleDeleteDivisionDialog,
                onEditDivisionTitle,
                onDuplicateDivision
              )}
            />
          )}
          {(hasDepartments || canEdit) && (
            <ActionButton
              className={classNames(
                { [classes.templateActionButton]: !canEdit },
                classes.actionButton
              )}
              type={ACTION_BUTTON_TYPES.ARROW}
              iconClass={classNames(classes.expandIcon, {
                [classes.expandIconOpened]: isExpanded,
              })}
            />
          )}
        </div>
      </div>
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <div className={classes.departmentsWrapper}>
          {hasDepartments && (
            <ReorderItems
              onSave={handleReorder}
              itemClass={classes.departmentItem}
              isDragDisabled={!canEdit}
            >
              {departments.map(department => (
                <Department
                  key={`department_item_${department.id}`}
                  id={department.id}
                  translations={translations}
                  department={department}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ))}
            </ReorderItems>
          )}
          {canEdit && (
            <CustomButton
              className={classes.button}
              type="addWithTextDarkNewMedium"
              onClick={() => handleCreateDepartment(division)}
            >
              {translations.createDepartmentButton}
            </CustomButton>
          )}
        </div>
      </Collapse>
    </div>
  );
};

Division.propTypes = {
  translations: PropTypes.object.isRequired,
  division: PropTypes.object.isRequired,
  isVisible: PropTypes.bool,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
  onDuplicateDivision: PropTypes.func,
  onReorderDepartment: PropTypes.func,
  handleCreateDepartment: PropTypes.func,
  handleDeleteDivisionDialog: PropTypes.func,
  onEditDivisionTitle: PropTypes.func,
  dragProps: PropTypes.object,
  dragRef: PropTypes.func,
  isExpandedOnFirstOpenDivision: PropTypes.bool,
};

export default Division;
